import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAllSubscriptionPlan, deleteSubscriptionPlan } from "../../../service/subscriptionService";
import { SubscriptionPlan } from "../../../types/subscriptionTypes";
import AddSubscriptionPlan from "./AddSubscriptionPlan";
import EditSubscriptionPlan from "./EditSubscriptionPlan";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import Layout from "../../../components/layouts/Layout";

const SubscriptionPlanList = () => {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [editPlan, setEditPlan] = useState<SubscriptionPlan | null>(null);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [planToDelete, setPlanToDelete] = useState<number | null>(null);

    const fetchPlans = async () => {
        try {
            const data = await getAllSubscriptionPlan();
            setPlans(data);
        } catch (error) {
            console.error("Failed to fetch subscription plans");
            toast.error("Failed to load subscription plans");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const handleEditPlan = (plan: SubscriptionPlan) => {
        setEditPlan(plan);
        setOpenEditModal(true);
    };

    const confirmDelete = (id: number) => {
        setPlanToDelete(id);
        setConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!planToDelete) return;
        try {
            await deleteSubscriptionPlan(planToDelete);
            toast.success("Subscription plan deleted successfully!");
            fetchPlans(); // Refresh the list
        } catch (error) {
            toast.error("Failed to delete subscription plan.");
        } finally {
            setConfirmOpen(false);
            setPlanToDelete(null);
        }
    };

    return (
        <Layout>
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Subscription Plan List</h2>
                <button
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                    onClick={() => setOpenAddModal(true)}
                >
                    Add New Plan
                </button>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-white shadow-md rounded-lg">
                    <table className="w-full">
                        <thead className="bg-gray-100">
                            <tr className="text-left">
                                <th className="p-3">#</th>
                                <th className="p-3">Title</th>
                                <th className="p-3">Description</th>
                                <th className="p-3">Price</th>
                                <th className="p-3">Duration</th>
                                <th className="p-3">Storage Size</th>
                                <th className="p-3 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((plan, index) => (
                                <tr key={plan.id} className="hover:bg-gray-50">
                                    <td className="p-3">{index + 1}</td>
                                    <td className="p-3">{plan.title}</td>
                                    <td className="p-3">{plan.descriptions}</td>
                                    <td className="p-3">${parseFloat(plan.price).toFixed(2)}</td>
                                    <td className="p-3">{plan.duration} year(s)</td>
                                    <td className="p-3">{plan.storage_size} KB</td>
                                    <td className="p-3 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                                                onClick={() => handleEditPlan(plan)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                                onClick={() => confirmDelete(plan.id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Delete Subscription Plan"
                message="Are you sure you want to delete this plan? This action cannot be undone."
            />

            {/* Add Subscription Plan Modal */}
            <AddSubscriptionPlan open={openAddModal} onClose={() => setOpenAddModal(false)} refreshPlans={fetchPlans} />

            {/* Edit Subscription Plan Modal */}
            <EditSubscriptionPlan open={openEditModal} onClose={() => setOpenEditModal(false)} plan={editPlan} refreshPlans={fetchPlans} />
        </Layout>
    );
};

export default SubscriptionPlanList;
